import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, Link as RouterLink, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { validatePassword, getPasswordPolicyDescription } from '../../utils/validationUtils';
import {
  Button,
  TextField,
  Link,
  Box,
  Typography,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { loginStart, loginSuccess, loginFailure } from '../../store/slices/authSlice';
import { fetchUserSuccess } from '../../store/slices/userSlice';
import { fetchCompanyStart, fetchCompanySuccess, fetchCompanyFailure } from '../../store/slices/companySlice';
import { showNotification } from '../../store/slices/uiSlice';
import { authService } from '../../services/authService';
import { fetchAndHydrateCurrentUser } from '../../services/fetchAndHydrateCurrentUser';
import { GET_COMPANY } from '../../graphql/queries';
import PasswordFieldWithSideToggle from '../../components/common/PasswordFieldWithSideToggle';
import LoginAnnouncement from '../../components/auth/LoginAnnouncement';

// パスワード変更ダイアログコンポーネント
const NewPasswordDialog: React.FC<{
  open: boolean;
  username: string;
  session: string | undefined;
  rememberMe: boolean;
  onSuccess: (result: any) => void;
  onError: (error: any) => void;
}> = ({ open, username, session, rememberMe, onSuccess, onError }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    // 入力検証
    if (!newPassword) {
      setError('新しいパスワードを入力してください');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    // パスワードポリシーの検証
    const validationResult = validatePassword(newPassword);
    if (!validationResult.isValid) {
      setError(validationResult.errorMessage || 'パスワードが要件を満たしていません');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // パスワード変更処理
      if (!session) {
        setError('認証セッションが見つかりません');
        onError({ message: '認証セッションが見つかりません' });
        return;
      }
      
      const result = await authService.completeNewPassword(username, newPassword, session, rememberMe);
      
      if (result.success) {
        onSuccess(result);
      } else {
        setError(result.message || 'パスワード変更に失敗しました');
        onError(result);
      }
    } catch (err) {
      setError('パスワード変更中にエラーが発生しました');
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">初回ログイン - パスワード変更</DialogTitle>
      <DialogContent>
        <DialogContentText>
          セキュリティのため、初回ログイン時にパスワードの変更が必要です。
          新しいパスワードを設定してください。
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2, mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
        <PasswordFieldWithSideToggle
          autoFocus
          margin="dense"
          density="dense"
          id="new-password"
          label="新しいパスワード"
          fullWidth
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          disabled={loading}
        />
        <PasswordFieldWithSideToggle
          margin="dense"
          density="dense"
          id="confirm-password"
          label="パスワード（確認）"
          fullWidth
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={loading}
        />
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          {getPasswordPolicyDescription()}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'パスワードを変更'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** ログイン後にリダイレクト可能なパスかどうかを判定 */
const isSafeRedirectPath = (path: string): boolean => {
  if (!path || path === '/') return false;
  if (path.startsWith('/login')) return false;
  if (path === '/forgot-password' || path.startsWith('/reset-password')) return false;
  return true;
};

const LoginPage: React.FC = () => {
  const { companyId: urlCompanyId } = useParams<{ companyId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const redirectPath = (location.state as { from?: string } | null)?.from;
  const isSsoPath = location.pathname === '/login/sso';
  const ssoToken = searchParams.get('token');
  const ssoCompany = searchParams.get('company');
  const ssoUser = searchParams.get('user');

  const saveCompanyIdToLocalStorage = (companyId: string) => {
    localStorage.setItem('lastCompanyId', companyId);
  };

  useEffect(() => {
    if (urlCompanyId || isSsoPath) return;
    const lastCompanyId = localStorage.getItem('lastCompanyId');
    if (lastCompanyId) {
      navigate(`/login/${lastCompanyId}`, { replace: true, state: location.state });
    }
  }, [urlCompanyId, isSsoPath, navigate, location.state]);
  
  const [companyId, setCompanyId] = useState(urlCompanyId || '');
  
  // URLパラメータが変更された場合に企業コードを更新
  useEffect(() => {
    if (urlCompanyId) {
      setCompanyId(urlCompanyId);
    }
  }, [urlCompanyId]);
  
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoTried, setSsoTried] = useState(false);

  const [showNewPasswordDialog, setShowNewPasswordDialog] = useState(false);
  const [authSession, setAuthSession] = useState<string | undefined>(undefined);

  const dispatch = useDispatch();
  const client = useApolloClient();

  // 企業IDとログインIDを結合してCognitoのユーザー名を生成
  const getCognitoUsername = (): string => {
    return `${companyId}#${userId}`;
  };

  const fetchCompanyInfo = async (companyId: string) => {
    try {
      dispatch(fetchCompanyStart());
      const companyResult = await client.query({
        query: GET_COMPANY,
        variables: { companyId }
      });
      
      if (companyResult.data?.getCompany) {
        dispatch(fetchCompanySuccess(companyResult.data.getCompany));
      }
    } catch (error) {
      dispatch(fetchCompanyFailure('Company情報の取得に失敗しました'));
    }
  };

  // ログイン後、JWT に載らない departments / profile を取得して Redux を更新する
  const fetchFullUserInfo = async (userId: string) => {
    await fetchAndHydrateCurrentUser(client, dispatch, userId);
  };

  // SSO: /login/sso?token=...&company=...&user=... のときワンクリックログイン
  useEffect(() => {
    if (!isSsoPath || !ssoToken || !ssoCompany || !ssoUser || ssoTried) return;
    setSsoTried(true);
    setSsoLoading(true);
    setError('');
    dispatch(loginStart());

    authService.loginWithSso(ssoCompany, ssoUser, ssoToken, rememberMe).then((result) => {
      if (result.success && result.token) {
        saveCompanyIdToLocalStorage(ssoCompany);
        dispatch(loginSuccess(result.token));
        if (result.user) {
          const completeUser = {
            ...result.user,
            displayName: result.user.displayName || result.user.email || 'ユーザー',
            disable: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          dispatch(fetchUserSuccess(completeUser));
          if (result.user.companyId) {
            fetchCompanyInfo(result.user.companyId);
          }
          if (result.user.userId) {
            fetchFullUserInfo(result.user.userId);
          }
        }
        dispatch(showNotification({ message: 'ログインに成功しました', type: 'success' }));
        navigate(isSafeRedirectPath(redirectPath ?? '') ? redirectPath! : '/', { replace: true });
      } else {
        dispatch(loginFailure(result.message || 'SSOログインに失敗しました'));
        setError(result.message || 'SSOログインに失敗しました');
      }
    }).catch((err) => {
      const msg = err instanceof Error ? err.message : 'SSOログイン中にエラーが発生しました';
      dispatch(loginFailure(msg));
      setError(msg);
    }).finally(() => {
      setSsoLoading(false);
    });
  }, [isSsoPath, ssoToken, ssoCompany, ssoUser, ssoTried, rememberMe, dispatch, navigate, redirectPath]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!companyId || !userId || !password) {
      setError('企業ID、ログインID、パスワードを入力してください');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      dispatch(loginStart());
      
      // 認証サービスを使用してログイン
      const result = await authService.login(getCognitoUsername(), password, rememberMe);
      
      if (result.success && result.token) {
        // 企業コードをローカルストレージに保存
        saveCompanyIdToLocalStorage(companyId);
        
        // 通常のログイン成功
        dispatch(loginSuccess(result.token));
        
        // ユーザー情報を設定
        if (result.user) {
          // ユーザー情報を補完
          const completeUser = {
            ...result.user,
            displayName: result.user.displayName 
              ? result.user.displayName 
              : result.user.email || 'ユーザー',
            disable: false,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          
          // デバッグ用：ユーザー情報をコンソールに出力
          // console.log('Login success - User info:', completeUser);
          dispatch(fetchUserSuccess(completeUser));
          
          // Company情報を取得
          if (result.user.companyId) {
            await fetchCompanyInfo(result.user.companyId);
          }
          if (result.user.userId) {
            await fetchFullUserInfo(result.user.userId);
          }
        }
        
        dispatch(showNotification({
          message: 'ログインに成功しました',
          type: 'success'
        }));
        
        navigate(isSafeRedirectPath(redirectPath ?? '') ? redirectPath! : '/', { replace: true });
      } else if (result.success) {
        // トークンがない場合（通常は発生しないはず）
        dispatch(showNotification({
          message: 'ログインに成功しましたが、認証トークンが見つかりません',
          type: 'warning'
        }));
        
        setError('認証トークンが見つかりません');
      } else if (result.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // 初回ログイン時のパスワード変更が必要
        if (result.session) {
          setAuthSession(result.session);
          setShowNewPasswordDialog(true);
        } else {
          dispatch(loginFailure('認証セッションが見つかりません'));
          setError('認証セッションが見つかりません');
        }
      } else {
        // その他のエラー
        dispatch(loginFailure(result.message || '認証に失敗しました'));
        setError(result.message || '認証に失敗しました');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '認証中にエラーが発生しました';
      dispatch(loginFailure(errorMessage));
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // パスワード変更成功時の処理
  const handlePasswordChangeSuccess = async (result: any) => {
    setShowNewPasswordDialog(false);
    
    if (result.token) {
      // 企業コードをローカルストレージに保存
      saveCompanyIdToLocalStorage(companyId);
      
      dispatch(loginSuccess(result.token));
      
      // ユーザー情報を設定
      if (result.user) {
        // ユーザー情報を補完
        const completeUser = {
          ...result.user,
          displayName: result.user.displayName || result.user.email || 'ユーザー',
          disable: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // デバッグ用：ユーザー情報をコンソールに出力
        // console.log('Password change success - User info:', completeUser);
        dispatch(fetchUserSuccess(completeUser));
        
        // Company情報を取得
        if (result.user.companyId) {
          await fetchCompanyInfo(result.user.companyId);
        }
        if (result.user.userId) {
          await fetchFullUserInfo(result.user.userId);
        }
      }
      
      dispatch(showNotification({
        message: 'パスワードが変更され、ログインに成功しました',
        type: 'success'
      }));
      
      navigate(isSafeRedirectPath(redirectPath ?? '') ? redirectPath! : '/', { replace: true });
    } else {
      // トークンがない場合（通常は発生しないはず）
      dispatch(showNotification({
        message: 'パスワードは変更されましたが、認証トークンが見つかりません',
        type: 'warning'
      }));
      
      setError('認証トークンが見つかりません');
    }
  };

  // パスワード変更エラー時の処理
  const handlePasswordChangeError = (error: any) => {
    dispatch(loginFailure(error.message || 'パスワード変更に失敗しました'));
  };

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const showSsoProgress = isSsoPath && ssoToken && ssoCompany && ssoUser;
  const ssoFailed = showSsoProgress && !ssoLoading && !!error;

  return (
    <>
      {error && <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }}>{error}</Alert>}

      {showSsoProgress && ssoLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress />
          <Typography>ログイン中…</Typography>
        </Box>
      ) : ssoFailed ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <Typography sx={{ textAlign: 'center' }}>
            通常のログインフォームからサインインしてください。
          </Typography>
          <Link
            component={RouterLink}
            to={`/login/${ssoCompany ?? ''}`}
            sx={{ color: (theme) => theme.palette.smartSkillTalk?.linkBlue ?? theme.palette.primary.main }}
          >
            ログインフォームへ
          </Link>
        </Box>
      ) : (
      <>
      {/* ユーザーへの告知エリア（メンテナンスのお知らせ等） */}
      <LoginAnnouncement />

      {/* ログインフォーム */}
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        px: { xs: 2, sm: 3 } // 画面幅に応じたパディングを追加（xs: モバイル, sm: タブレット以上）
      }} className="inputs">
        <Box className="input-field company-code" sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px 15px',
          width: '450px',
          maxWidth: '100%',
          height: '46px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #999999',
          borderRadius: '4px',
        }}>
          <TextField
            required
            fullWidth
            id="companyId"
            placeholder="企業コードを入力してください"
            name="companyId"
            autoComplete="organization"
            autoFocus={!urlCompanyId}
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={loading}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{}}
          />
        </Box>
        
        <Box className="input-field user-id" sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '10px 15px',
          width: '450px',
          maxWidth: '100%',
          height: '46px',
          backgroundColor: '#FFFFFF',
          border: '1px solid #999999',
          borderRadius: '4px',
        }}>
          <TextField
            required
            fullWidth
            id="userId"
            placeholder="IDを入力してください"
            name="userId"
            autoComplete="username"
            autoFocus={!!urlCompanyId}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            disabled={loading}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            sx={{}}
          />
        </Box>
        
        <Box className="password-container" sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          width: '450px',
          maxWidth: '100%',
          mb: 0.5
        }}>
          <Box className="input-field password" sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '10px 15px',
            width: 'calc(100% - 56px)',
            height: '46px',
            backgroundColor: '#FFFFFF',
            border: '1px solid #999999',
            borderRadius: '4px 0px 0px 4px',
          }}>
            <TextField
              required
              fullWidth
              name="password"
              placeholder="パスワードを入力してください"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              sx={{}}
            />
          </Box>
          <Box 
            className="password-eye"
            onClick={handleClickShowPassword}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '9px 13px',
              width: '52px',
              height: '46px',
              backgroundColor: '#FFFFFF',
              border: '1px solid #999999',
              borderRadius: '0px 4px 4px 0px',
              cursor: 'pointer',
            }}
          >
            {showPassword ? <VisibilityOff sx={{ color: '#666666' }} /> : <Visibility sx={{ color: '#666666' }} />}
          </Box>
        </Box>
        
        <Box className="forgot-password" sx={{ mt: 0.5, textAlign: 'center' }}>
          <Link 
            component={RouterLink} 
            to="/forgot-password" 
            className="forgot-password-link"
            sx={{ 
              color: theme => theme.palette.smartSkillTalk.linkBlue,
            }}
          >
            ※パスワードを忘れた場合はこちら
          </Link>
        </Box>

        <Box className="button-container" sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px 0px 30px',
        }}>
          <Button
            type="submit"
            variant="contained"
            color="success"
            disabled={loading}
            className="signin-button"
            sx={{ 
              backgroundColor: theme => theme.palette.smartSkillTalk.green,
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'サインイン'}
          </Button>
        </Box>
      </Box>
      
      {/* 初回ログイン時のパスワード変更ダイアログ */}
      <NewPasswordDialog
        open={showNewPasswordDialog}
        username={getCognitoUsername()}
        session={authSession}
        rememberMe={rememberMe}
        onSuccess={handlePasswordChangeSuccess}
        onError={handlePasswordChangeError}
      />
      </>
      )}
    </>
  );
};

export default LoginPage;
